import { useMemo } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { DISCLAIMER_LONG_NL_UI } from '../lib/legal-copy';
import { FileText, Home } from 'lucide-react';
import { AnimatedBar } from '../components/AnimatedBar';
import {
  loadComfortState,
  USER_SEGMENT_LABELS,
  RUIMTE_POSITIE_LABELS,
  JIP_EN_JANNEKE_LEGEND,
  type TechniekAdviesCategorie,
} from '../lib/comfortCalc';

const ADVIES_CAT_NL: Record<TechniekAdviesCategorie, string> = {
  officieel: 'Regels & officiële trajecten',
  installatie: 'Installatie op locatie',
  warmtebron: 'Warmtebron',
  afgifte: 'Afgifte',
  koeling: 'Koeling',
  combinatie: 'Combinatie',
  ventilatie: 'Ventilatie',
  overig: 'Overig',
};

function verdiepingLabel(v: number): string {
  if (v <= 1) return 'Begane grond';
  if (v === 2) return '1e verdieping';
  return `${v - 1}e verdieping`;
}

export function RapportPage() {
  const navigate = useNavigate();
  const data = useMemo(() => loadComfortState(), []);
  const kamersPerVerdieping = useMemo(() => {
    if (!data?.result) return [];
    const rows = data.result.ruimteLasten;
    const m = new Map<number, typeof rows>();
    for (const r of rows) {
      const f = r.verdieping ?? 1;
      const arr = m.get(f) ?? [];
      arr.push(r);
      m.set(f, arr);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [data?.result]);
  if (!data?.result) {
    return <Navigate to="/scan" replace />;
  }
  const { input, result } = data;
  const gi = result.gebouwIntensiteit;
  const maxW = Math.max(...result.ruimteLasten.map((r) => r.warmte_W), 1);
  const maxK = Math.max(...result.ruimteLasten.map((r) => r.koel_W), 1);

  return (
    <div className="report-sheet bg-ct-bg px-4 py-6 text-ct-text print:py-4">
      <Seo
        path="/rapport"
        title="Printrapport"
        description="Print of PDF: ComfortTool™ warmte- en koeloverzicht en samenvatting uit je opgeslagen scan."
        keywords="ComfortTool print, PDF, rapport, warmtelast, koellast"
      />
      <div className="report-no-print mx-auto mb-6 flex max-w-4xl flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-xl border border-ct-border px-4 py-2 text-sm font-medium text-ct-muted hover:bg-ct-elevated"
        >
          ← Terug
        </button>
        <div className="flex gap-2">
          <Link
            to="/scan"
            className="inline-flex items-center gap-2 rounded-xl border border-ct-border px-4 py-2 text-sm font-medium text-ct-muted hover:bg-ct-elevated"
          >
            <Home className="h-4 w-4" aria-hidden />
            Naar scan
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-xl bg-ct-accent px-4 py-2 text-sm font-bold text-white hover:bg-blue-500"
          >
            Print / PDF
          </button>
        </div>
      </div>

      <div className="report-print-root">
      <article className="report-sheet mx-auto max-w-4xl space-y-10 rounded-3xl border border-ct-border bg-ct-surface p-8 shadow-2xl print:border-0 print:shadow-none print:p-4">
        <header className="border-b border-ct-border pb-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ct-accent/15 text-ct-accent print:border print:border-gray-300">
              <FileText className="h-6 w-6" aria-hidden />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-ct-accent">ComfortTool</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight">Jouw warmte- en koeloverzicht</h1>
            </div>
          </div>
          <p className="report-muted mt-3 text-sm text-ct-muted">
            Ingevuld als: {USER_SEGMENT_LABELS[result.userSegment]} · rapportdatum{' '}
            {new Date().toLocaleDateString('nl-NL', { dateStyle: 'long' })}
          </p>
          <p className="report-muted mt-2 text-xs text-ct-muted">
            Geen officieel energielabel — gebruik dit om te overleggen met collega&apos;s, opdrachtgevers of bewoners.
          </p>
        </header>

        <section>
          <h2 className="text-xl font-bold">Samenvatting (totaal huis)</h2>
          <p className="report-muted mt-3 text-sm leading-relaxed text-ct-muted">{result.jipEnJannekeSamenvatting}</p>
          <div className="report-card mt-6 grid gap-4 rounded-2xl border border-ct-border bg-ct-elevated/40 p-6 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-wide text-ct-muted">Warmtelast (piek)</p>
              <p className="mt-1 text-2xl font-bold text-ct-text">{result.warmtelastKW} kW</p>
              <p className="text-xs text-ct-muted">{gi.warmte_W_m2} W/m² · {gi.warmte_W_m3} W/m³</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-ct-muted">Koellast (piek)</p>
              <p className="mt-1 text-2xl font-bold text-ct-text">{result.koellastKW} kW</p>
              <p className="text-xs text-ct-muted">{gi.koel_W_m2} W/m² · {gi.koel_W_m3} W/m³</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-ct-muted">Volume</p>
              <p className="mt-1 text-2xl font-bold text-ct-text">{gi.volume_m3} m³</p>
              <p className="text-xs text-ct-muted">Vloer {input.vloeroppervlakte} m²</p>
            </div>
          </div>
          <div className="report-card mt-4 overflow-x-auto rounded-2xl border border-ct-border">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ct-border bg-ct-bg/50 text-ct-muted">
                  <th className="px-4 py-3">Gebouw</th>
                  <th className="px-4 py-3">Warmte W</th>
                  <th className="px-4 py-3">W/m²</th>
                  <th className="px-4 py-3">W/m³</th>
                  <th className="px-4 py-3">Koel W</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-ct-border/60">
                  <td className="px-4 py-3 font-medium">Hele woning</td>
                  <td className="px-4 py-3 font-mono tabular-nums">
                    {Math.round(result.warmtelastKW * 1000).toLocaleString('nl-NL')}
                  </td>
                  <td className="px-4 py-3 font-mono tabular-nums">{gi.warmte_W_m2}</td>
                  <td className="px-4 py-3 font-mono tabular-nums">{gi.warmte_W_m3}</td>
                  <td className="px-4 py-3 font-mono tabular-nums">
                    {Math.round(result.koellastKW * 1000).toLocaleString('nl-NL')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {result.segmentRegelgevingKort ? (
          <section className="rounded-2xl border border-dashed border-ct-border bg-ct-bg/20 p-5">
            <h2 className="text-xl font-bold">Wat speelt er vaak mee (afhankelijk van wie je bent)</h2>
            <p className="report-muted mt-3 text-sm leading-relaxed text-ct-muted">
              {result.segmentRegelgevingKort}
            </p>
          </section>
        ) : null}

        <section>
          <h2 className="text-xl font-bold">Per kamer — ook per verdieping</h2>
          <p className="report-muted mt-2 text-sm text-ct-muted">
            Zelfde totaal als hierboven — verdeeld over {result.ruimteLasten.length}{' '}
            {result.ruimteLasten.length === 1 ? 'vlak' : 'kamers'}. Meerdere verdiepingen staan onder elkaar gegroepeerd
            zodat je in één oogopslag een verdieping kunt bespreken.
          </p>
          <div className="mt-6 space-y-10">
            {kamersPerVerdieping.map(([vloer, kamers]) => (
              <div key={vloer}>
                <h3 className="text-sm font-bold uppercase tracking-wide text-ct-accent">
                  {verdiepingLabel(vloer)}
                </h3>
                <div className="mt-4 space-y-6">
                  {kamers.map((r) => (
              <div
                key={r.id}
                className="report-card rounded-2xl border border-ct-border bg-ct-bg/30 p-5"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-ct-text">{r.naam}</h3>
                    <p className="text-xs text-ct-muted">
                      {r.vloeroppervlakte_m2} m² · {RUIMTE_POSITIE_LABELS[r.positie]} · verdieping {r.verdieping ?? 1}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <p>
                      <span className="text-ct-muted">piek </span>
                      <strong className="text-ct-text">{(r.piek_W / 1000).toFixed(2)} kW</strong>
                    </p>
                  </div>
                </div>
                <p className="report-muted mt-3 text-sm italic leading-relaxed text-ct-muted">{r.uitleg_regel}</p>
                <p className="report-muted mt-2 text-xs leading-relaxed text-ct-muted">
                  Warmte: {Math.round(r.warmte_transmissie_W)} W via muren en ramen + {Math.round(r.warmte_ventilatie_W)} W
                  door lucht die wisselt · Koel: {Math.round(r.koel_transmissie_W)} + {Math.round(r.koel_ventilatie_W)} +{' '}
                  {Math.round(r.koel_zon_W)} W (muren/ramen, lucht, zon op ramen)
                </p>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold text-ct-muted">Warmte {r.warmte_W.toLocaleString('nl-NL')} W</p>
                    <div className="mt-1">
                      <AnimatedBar
                        ratio={r.warmte_W / maxW}
                        className="bg-gradient-to-r from-ct-warn to-orange-500"
                        delayMs={40}
                        ariaLabel={`Warmte ${r.naam}`}
                      />
                    </div>
                    <p className="mt-1 text-xs text-ct-muted">{r.warmte_W_m2} W/m² · jaar {r.jaar_warmte_kWh} kWh</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-ct-muted">Koel {r.koel_W.toLocaleString('nl-NL')} W</p>
                    <div className="mt-1">
                      <AnimatedBar
                        ratio={r.koel_W / maxK}
                        className="bg-gradient-to-r from-sky-500 to-blue-500"
                        delayMs={100}
                        ariaLabel={`Koeling ${r.naam}`}
                      />
                    </div>
                    <p className="mt-1 text-xs text-ct-muted">{r.koel_W_m2} W/m² · jaar {r.jaar_koel_kWh} kWh</p>
                  </div>
                </div>
              </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold">Installatie-opties en technieken</h2>
          <p className="report-muted mt-2 text-sm text-ct-muted">
            Indicatief advies op basis van de berekende lasten en het ingevulde gebouw — combineer met je eigen voorkeur,
            budget en lokale regels.
          </p>
          <div className="mt-4 space-y-3">
            {(result.adviesTechnieken ?? []).map((a, i) => (
              <div
                key={`${a.categorie}-${i}`}
                className="report-card rounded-xl border border-ct-border bg-ct-elevated/30 p-4"
              >
                <p className="text-xs font-bold uppercase tracking-wide text-ct-accent">
                  {ADVIES_CAT_NL[a.categorie]}
                </p>
                <h3 className="mt-1 text-base font-bold text-ct-text">{a.titel}</h3>
                <p className="report-muted mt-2 text-sm leading-relaxed text-ct-muted">{a.toelichting}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold">Woordenlijst (Jip-en-Janneke)</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {JIP_EN_JANNEKE_LEGEND.map((item) => (
              <div key={item.titel} className="report-card rounded-xl border border-ct-border bg-ct-elevated/30 p-4">
                <h3 className="font-bold text-ct-accent">{item.titel}</h3>
                <p className="report-muted mt-2 text-sm leading-relaxed text-ct-muted">{item.tekst}</p>
              </div>
            ))}
          </div>
        </section>

        {result.climateContext && (
          <section className="rounded-2xl border border-dashed border-ct-border p-5">
            <h2 className="text-lg font-bold">Klimaat (regio)</h2>
            <p className="report-muted mt-2 text-sm text-ct-muted">
              {result.climateContext.locationLabel} — HDD {result.climateContext.hdd18}, CDD{' '}
              {result.climateContext.cdd24}. Jaartotalen per kamer zijn meegeschaald.
            </p>
          </section>
        )}

        <footer className="border-t border-ct-border pt-6 text-xs leading-relaxed text-ct-muted">
          <p>{result.methodologyNote}</p>
          <p className="mt-3 font-medium ct-caution-text print:text-amber-900">{result.disclaimer}</p>
          <p className="mt-4 text-ct-muted/90 print:text-gray-700">{DISCLAIMER_LONG_NL_UI}</p>
          <p className="mt-3 print:text-gray-800">
            Zie ook{' '}
            <Link to="/voorwaarden" className="text-ct-accent hover:underline print:text-blue-800">
              algemene voorwaarden
            </Link>
            .
          </p>
        </footer>
      </article>
      </div>
    </div>
  );
}
